import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_POS,
  RECEIVE_ALL_POS,
  RECEIVE_PO_METADATA,
  RECEIVE_PO_FAILURE,
  INCREMENT_PO_COUNT,
  RECEIVE_PO_LINES,
  RECEIVE_ALL_PO_LINES,
  RECEIVING_PO_LINES,
  RECEIVE_PO_LINES_METADATA,
  RECEIVE_PO_LINES_FAILURE,
  SAVING_PO_LINES,
  SAVING_PO_LINES_FAILURE,
  UPDATING_PO_LINES,
  UPDATING_PO_LINES_FAILURE,
  CLEAR_PO_STATE,
  CLEAR_STATE,
  CLEAR_ALL_PO_LINES_STATE,
  CLEAR_PO_UPDATE_ERR_STATE,
  UPDATE_CURRENT_PO_PAGE,
  RESET_PO_META,
  DELETE_PO_LINES,
  CLEAR_PO_TRX_LINES,
  FILTER_PO_QUERY,
  CLEAR_FILTER_PO_QUERY
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import { fetchLinesNew, getTrxIds } from '@/store/modules/helpers/actionHelpers'
import {
  receiveData,
  increment,
  receiveMetadata,
  resetMetadata,
  receiveLinesMetadata,
  deleteLine,
  clearTrxLines
} from '@/store/modules/helpers/mutationHelpers'

const lineSchema = new schema.Entity('lines')
const lineListSchema = new schema.Array(lineSchema)
const poSchema = new schema.Entity('pos')
const poListSchema = new schema.Array(poSchema)

const state = {
  data: {},
  all: [],
  allLines: [],
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  receiveDataFailure: false,
  lines: {},
  lineCount: null,
  nextLineUrl: null,
  prevLineUrl: null,
  savingLines: null,
  updatingLine: false,
  updatingLineFailure: null,
  receivingLines: false,
  receivingLinesFailure: null,
  filterInfo: {}
}

const actions = {
  /**
   * Fetches one or more POs with the option to pass query string filter parameters
   * @param store
   * @param opts
   * @return {Promise<*>}
   */
  async fetchPosWithQuery (store, opts) {
    let query
    let storeLines = false
    if (opts) {
      query = opts.hasOwnProperty('query') ? opts.query : opts
      storeLines = opts.hasOwnProperty('storeLines') ? opts.storeLines : false
    } else {
      query = {}
    }
    query.nest_lines = false
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.get(buildUrlWithQuery('pos', query, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      this.commit(RESET_PO_META)
      const { results, next, previous, count } = resp
      const { entities, result } = normalize(results, poListSchema)
      if (!(result && Array.isArray(result))) {
        throw new Error('No result set in response')
      }
      let mutationPayload = { data: entities.pos }
      if (query && query.page && typeof query.page === 'number') {  // If query contains a page number
        mutationPayload.page = query.page                           // pass the page number to the mutation handler
      }
      this.commit(RECEIVE_POS, mutationPayload)
      this.commit(RECEIVE_PO_METADATA, { next, previous, count, result: result.length })
      this.commit(UPDATE_CURRENT_PO_PAGE, query && query.page || 1)
      if (storeLines) {
        const lines = await fetchLinesNew.bind(this)({ trxIds: getTrxIds(entities.pos), type: 'po' })
        this.commit(RECEIVE_PO_LINES, { id: null, data: lines })
      }
      return { query, count, entities, previous, next }
    } catch (e) {
      console.log(e)
      this.commit(RECEIVE_PO_FAILURE, handleXHRError(e))
    }
  },
  /**
   * TODO: this doesn't account for multiple pages. Needs to be fixed
   * Fetches one or more PO lines with the option to pass query string filter parameters
   * @param store
   * @param query
   * @return {Promise<*>}
   */
  async fetchPoLinesWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_PO_LINES, true)
    try {
      const resp = await Vue.http.get(buildUrlWithQuery('lines/po', query, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      this.commit(RECEIVING_PO_LINES, false)
      if (!resp.hasOwnProperty('results')) {
        throw new Error("Expected response to contain a 'results' property, but none was found.")
      }
      const { results, next, previous, count } = resp
      const { entities, result } = normalize(results, lineListSchema)
      this.commit(RECEIVE_PO_LINES, !result || !result.length
        ? { id: null, data: results }
        : { id: null, data: entities.lines })
      this.commit(RECEIVE_PO_LINES_METADATA, { next, previous, count, result: result.length })
      return { query, count, entities }
    } catch (e) {
      console.log(e)
      this.commit(RECEIVING_PO_LINES, false)
      this.commit(RECEIVE_PO_LINES_FAILURE, handleXHRError(e))
    }
  },
  fetchAllPos (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrl('pos?serialize=false&nest_lines=false', null, instanceUrl), { headers })
      .then(checkStatus)
      .then(response => {
        if (!response || !Array.isArray(response.body)) return this.commit(RECEIVE_PO_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_ALL_POS, { data: response.body })
        return response.body
      })
      .catch(e => {
        this.commit(RECEIVE_PO_FAILURE, e.statusText)
      })
  },
  fetchAllPoLines (store) {
    // Use unserailized data to get all POs to get the amount_remaining
    this.commit(CLEAR_ALL_PO_LINES_STATE, true)

    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_PO_LINES, true)

    return Vue.http({ url: buildUrl('lines/po?meta_choices=True&nest_lines=false', null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(response => {
        this.commit(RECEIVING_PO_LINES, false)
        if (!response || !response.hasOwnProperty('choices')) return this.commit(RECEIVE_PO_LINES_FAILURE, 'Error fetching POs')
        if (!Array.isArray(response.choices)) return this.commit(RECEIVE_PO_LINES_FAILURE, 'Error normalizing response')
        this.commit(RECEIVE_ALL_PO_LINES, { data: response.choices })
        return response.choices
      })
      .catch(e => {
        this.commit(RECEIVING_PO_LINES, false)
        this.commit(RECEIVE_PO_LINES_FAILURE, handleXHRError(e))
      })
  },
  notifyPos (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.post(buildUrl('notify/purchase_order', null, instanceUrl), payload, { headers })
      .then(r => {
        return r.body
      })
      .catch(e => {
        console.log(e)
      })
  },
  /**
   * Saves a PO transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async savePo (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.post(buildUrl('pos', null, instanceUrl), payload, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to save PO`)
      }
      const { entities, result } = normalize(resp, poSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when saving PO`)
      }
      this.commit(RECEIVE_POS, { data: entities.pos })
      if (resp && resp.lines) {
        const e = normalize(resp.lines, lineListSchema)
        this.commit(RECEIVE_PO_LINES, { id: resp.id, data: e.entities.lines })
      }
      this.commit(INCREMENT_PO_COUNT, 1)
      return resp
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Updates an PO transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async updatePo (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.patch(buildUrl(`pos/${id}`, null, instanceUrl), data, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if ((resp && resp.message && resp.message.includes('Updated the data'))) {
        return resp
      }
      if (!resp) {
        throw new Error(`Unable to update PO with id ${id}`)
      }
      const { entities, result } = normalize(resp, poSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when updating PO with id ${id}`)
      }
      this.commit(RECEIVE_POS, { data: entities.pos })
      if (resp && resp.lines) {
        const e = normalize(resp.lines, lineListSchema)
        this.commit(RECEIVE_PO_LINES, { id: resp.id, data: e.entities.lines })
      }
      return resp
    } catch (e) {
      console.log(e)
      return handleXHRError(e)
    }
  },
  /**
   * Updates a PO line
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async updatePoLine (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    this.commit(UPDATING_PO_LINES, { lineId: id, status: true })
    return Vue.http.patch(buildUrl(`lines/po/${id}`, null, instanceUrl), data, { headers })
      .then(r => {
        this.commit(UPDATING_PO_LINES, { id, status: false })
        if (r && r.status === 200) {
          const { entities, result } = normalize(r.body, poSchema)
          if (!result) return this.commit(RECEIVE_POS, r.body)
          this.commit(RECEIVE_PO_LINES, entities.lines)
          this.commit(RECEIVE_PO_LINES_METADATA)
          return r.body
        } else {
          this.commit(RECEIVE_PO_LINES_FAILURE, r ? r.statusText : 'Failed to update PO line')
        }
      })
      .catch(e => {
        this.commit(UPDATING_PO_LINES, { poId: id, status: false })
        this.commit(UPDATING_PO_LINES_FAILURE, { id, err: handleXHRError(e) })
      })
  },
  async convertToAP (store, id) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.post(buildUrl(`pos/${id}/convert_to_ap`, null, instanceUrl), null, { headers })
      if (!resp) {
        throw new Error(`Unable to convert PO with id ${id}`)
      }
      return resp
    } catch (e) {
      return handleXHRError(e)
    }
  },
  /**
   * Check a PO transaction can be deleted
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async checkPoCanDelete (store, payload) {
    const { id } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return Vue.http.get(buildUrl(`entry/po_info_validate/${id}`, null, instanceUrl), { headers })
      .then(r => {
        if (r && r.status === 200) {
          return r.body
        }
      })
      .catch(e => {
        console.log(e)
      })
  },
  /**
   * Deletes a PO transaction Permanently
   * @param store
   * @param id
   * @return {Promise<*>}
   */
  async deletePo (store, payload) {
    const { id } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    return await Vue.http.delete(buildUrl(`entry/po_info_validate/${id}`, {deletion_type: 'True'}, instanceUrl), { headers })
    .then(r => {
      if (r && r.status === 200) {
        return r.body
      }
    })
    .catch(e => {
      console.log(e)
    })
  },
  clearPoState (store, payload = null) {
    this.commit(CLEAR_PO_STATE, payload)
  },
  clearPoTransactionLines (store, apId) {
    this.commit(CLEAR_PO_TRX_LINES, apId)
  },
  clearAllPoLinesState (store, payload = null) {
    return this.commit(CLEAR_ALL_PO_LINES_STATE, payload)
  },
  resetPoUpdateErrState () {
    this.commit(CLEAR_PO_UPDATE_ERR_STATE)
  },
  resetPoMeta () {
    return this.commit(RESET_PO_META)
  },
  setPoFilterInfo (store, query) {
    this.commit(FILTER_PO_QUERY, query)
  },
  clearSetPoFilterInfo (store) {
    this.commit(CLEAR_FILTER_PO_QUERY)
  }
}

const getters = {
  getPoFilterInfo: (state) => {
    return state.filterInfo
  },
  getPos: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getPoById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllPos: (state) => {
    return state.all || []
  },
  getAllPoLines: (state) => {
    return state.allLines || []
  },
  getPoMetadata: (state) => {
    const { shown, count, next, previous } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown, next, previous }
  },
  getPoLines: (state) => {
    return state.lines ? Object.values(state.lines).map(v => Object.assign({}, v)) : []
  },
  getPoLineById: (state) => (id) => {
    const { lines } = state
    for (let l in lines) {
      if (!lines.hasOwnProperty(l)) continue
      if (parseInt(lines[l].id) === parseInt(id)) return lines[l]
    }
    return {}
  },
  getPoLinesByTransactionId: (state) => (id) => {
    const { lines } = state
    if (!Object.keys(state.lines).length) return {}
    let ret = {}
    for (let l in state.lines) {
      if (lines[l].hasOwnProperty('transaction_id') && parseInt(lines[l].transaction_id) === parseInt(id)) {
        ret[l] = lines[l]
      }
    }
    return ret
  },
  getPoLinesBySearchCriteria: (state) => (query) => {
    return Object.values(state.lines).filter(v => {
      return Object.keys(v).some(k => {
        if (v[k] && v[k].toString().toLowerCase().includes(query.toLowerCase())) return v
        return false
      })
    })
  },
  getPoLinesFetchStatus: (state) => {
    return state.receivingLines
  },
  getNextPoLineSet: (state) => {
    return state.nextLineUrl
  },
  getPrevPoLineSet: (state) => {
    return state.prevLineUrl
  },
  getPosByNumber: (state) => (number) => {
    return state.data ? Object.values(state.data).filter(v => v.number.includes(number)) : []
  },
  getCurrentPoPage: (state) => {
    if (state.currentPage) return state.currentPage
  }
}

const mutations = {
  [CLEAR_FILTER_PO_QUERY] (state) {
    state.filterInfo = {}
  },
  [FILTER_PO_QUERY] (state, query) {
    if (query.hasOwnProperty('keyword')) {
      state.filterInfo.keyword = query.keyword
    }
    if (query.hasOwnProperty('status__in')) {
      state.filterInfo.status__in = query.status__in
    }
    if (query.hasOwnProperty('batch')) {
      state.filterInfo.batch = query.batch
    }
    if (query.hasOwnProperty('vendor')) {
      state.filterInfo.vendor = query.vendor
    }
    if (query.hasOwnProperty('page')) {
      state.filterInfo.page = query.page
    }
    if (query.hasOwnProperty('batch__bank__currency__in') && query.batch__bank__currency__in.length) {
      state.filterInfo.batch__bank__currency__in = query.batch__bank__currency__in
    }
  },
  [RECEIVE_POS] (state, results) {
    for (let d in results.data) {
      results.data[d]['transactionType'] = 'Purchase Order'
    }
    receiveData(state, results)
  },
  [RECEIVE_ALL_POS] (state, results) {
    const { data } = results
    state.all = data
    return true
  },
  [RECEIVE_PO_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [RECEIVE_PO_LINES] (state, payload) {
    const { id, data } = payload
    if (!(data && (Array.isArray(data) || Object.keys(data).length))) {
      return state.lines
    }
    let tempLineState = { ...state.lines }
    let newData = {}
    if (Array.isArray(data) && data.every(line => !!line.id)) {
      data.forEach(line => (newData[line.id] = line))
    } else {
      newData = data
    }
    if (tempLineState && Object.keys(tempLineState).length) {
      tempLineState = { ...tempLineState, ...newData }
    } else {
      tempLineState = { ...newData }
    }
    if (id) {
      let tempState = { ...state.data }
      for (let d in newData) {
        if (tempState[id] && tempState[id].hasOwnProperty('lines') && Array.isArray(tempState[id].lines)) {
          tempState[id].lines = [...new Set([...tempState[id].lines, newData[d].id])]
        }
      }
      state.data = { ...tempState }
    }
    state.lines = { ...tempLineState }
  },
  [RECEIVING_PO_LINES] (state, payload) {
    state.receivingLines = payload
  },
  [RECEIVE_ALL_PO_LINES] (state, results) {
    const { data } = results
    for (let line in data) {
      if (state.allLines.find(({id}) => id === line.id)) {
        continue
      } else {
        state.allLines.push(data[line])
      }
    }
    return true
  },
  [RECEIVE_PO_LINES_FAILURE] (state, payload) {
    state.receivingLines = payload
  },
  [SAVING_PO_LINES] (state, payload) {
    state.savingLines = payload
  },
  [SAVING_PO_LINES_FAILURE] (state, payload) {
    state.savingLines = { status: false, err: payload }
  },
  [UPDATING_PO_LINES] (state, payload) {
    state.updatingLine = payload
  },
  [UPDATING_PO_LINES_FAILURE] (state, payload) {
    state.updateLineFailure = payload
  },
  [INCREMENT_PO_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_PO_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [RECEIVE_PO_LINES_METADATA] (state, payload) {
    receiveLinesMetadata(state, payload)
  },
  [UPDATE_CURRENT_PO_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [CLEAR_PO_STATE] (state, payload) {
    let newState = {
      data: Object.assign({}),
      lines: Object.assign({}),
      savingLines: false,
      receiveDataFailure: false,
      deleting: false,
      updatingLine: false,
      updatingLineFailure: false,
      receivingLines: false,
      receivingLinesFailure: false
    }
    if (payload) {
      if (Array.isArray(payload) && payload.length) {
        payload.forEach(v => {
          if (newState.hasOwnProperty(v)) delete newState[v]
        })
      } else {
        newState['cache'] = Object.assign({})
      }
    }
    for (let key in newState) {
      state[key] = newState[key]
    }
  },
  [CLEAR_PO_TRX_LINES] (state, payload) {
    clearTrxLines(state, payload)
  },
  [RESET_PO_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    state.data = Object.assign({})
    state.cache = Object.assign({})
    state.lines = Object.assign({})
    state.all = []
    state.allLines = []
    state.count = null
    state.lineCount = null
    state.shown = null
    state.next = null
    state.previous = null
    state.currentPage = 1
    state.receiveDataFailure = false
    state.nextLineUrl = null
    state.prevLineUrl = null
    state.savingLines = null
    state.updatingLine = false
    state.updatingLineFailure = null
    state.receivingLines = false
    state.receivingLinesFailure = null
  },
  [CLEAR_ALL_PO_LINES_STATE] (state) {
    state.allLines = []
  },
  [CLEAR_PO_UPDATE_ERR_STATE] (state) {
    state.updateFailure = null
  },
  [DELETE_PO_LINES] (state, payload) {
    deleteLine(state, payload)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/po.js